// Version with default value: data is always DataType
export declare class LocalStorageWithDefault<DataType extends object> {
  key: string;
  defaultValue: DataType;

  constructor(key: string, defaultValue: DataType);

  get(): DataType;
  set(value: DataType): void;

  exist(): boolean;

  update(update: Partial<DataType>): void;
  remove(): void;
}

// Version without default: data may be null
export declare class LocalStorageNullable<DataType extends object> {
  key: string;

  constructor(key: string);

  get(): DataType | null;
  set(value: DataType): void;

  exist(): boolean;

  update(update: Partial<DataType>): void;
  remove(): void;
}

// A single implementation backing both
class LocalStorageImpl<DataType extends object> {
  key: string;
  defaultValue?: DataType;

  constructor(key: string, defaultValue?: DataType) {
    this.key = key;
    this.defaultValue = defaultValue;

    if (defaultValue !== undefined && !localStorage.getItem(this.key)) {
      localStorage.setItem(this.key, JSON.stringify(defaultValue));
    }
  }

  exist() {
    return localStorage.getItem(this.key) !== null;
  }

  get(): DataType | null {
    const raw = localStorage.getItem(this.key);
    if (!raw) {
      return this.defaultValue ?? null;
    }
    return JSON.parse(raw) as DataType;
  }

  set(data: DataType) {
    localStorage.setItem(this.key, JSON.stringify(data));
  }

  update(update: Partial<DataType>) {
    const currentData = (this.get() ?? {}) as DataType;
    const finalData = {...currentData, ...update} as DataType;
    this.set(finalData);
  }

  remove() {
    localStorage.removeItem(this.key);
  }
}

// Factory type union
export const LocalStorage: {
  new <T extends object>(key: string): LocalStorageNullable<T>;
  new <T extends object>(key: string, defaultValue: T): LocalStorageWithDefault<T>;
} = LocalStorageImpl as any;

export const selectedItemsLocalStorage = new LocalStorage<string[]>('selectedItems', []);
